<template>
  <input-custom v-bind:id="id"
                v-bind:label="label"
                v-bind:language="language"
                v-bind:required="required">
    <div v-bind:class="{'is-invalid': invalid}">
      <input type="hidden"
             v-bind:value="value"
             v-on:input="updateValue($event.target.value);"/>
      <div v-bind:class="{'not-dragging': !dragging, 'dragging': dragging, 'drag-target': onDragTarget}">
        <i v-show="dragging" class="fal fa-cloud-upload"></i>
        <p v-show="dragging">{{ multiple ? bundle.id_11739 : bundle.id_11738 }}</p>
        <div class="btn btn-primary me-2" v-bind:class="{'disabled': maxFilesReached}">
          {{ emptyValue ? multiple ? bundle.id_11746 : bundle.id_10587 : multiple ? bundle.id_11747 : bundle.id_10586 }}
          <i v-if="uploading" class="fa-loading-white-12 ms-2"/>
          <input v-bind:id="id"
                 name="file"
                 type="file"
                 v-bind:multiple="multiple"
                 style="border: none; height: 100%; left: 0; opacity: 0; position: absolute; top: 0; width: 100%; z-index: 2;"
                 ref="file"
                 v-on:click="$refs.file.value = null"
                 v-on:change="handleUpload()"
                 class="dropzone"
                 v-bind:class="{'is-invalid': invalid}"/>
        </div>
      </div>
      <button v-if="!dragging && !multiple && !emptyValue"
              type="button"
              v-on:click.prevent="removeValue()"
              style="display: inline-block;"
              class="btn btn-outline-danger">
        {{ bundle.id_10588 }}
      </button>
      <div v-show="!dragging" ref="preview">
        <div v-if="uploading" style="margin: 1rem 0;" class="progress">
          <div class="progress-bar progress-bar-striped active"
               role="progressbar"
               v-bind:aria-valuenow="uploadProgress"
               aria-valuemin="0"
               aria-valuemax="100"
               v-bind:style="'width: ' + uploadProgress + '%'">
            {{ uploadProgress }}%
          </div>
        </div>
        <template v-if="!externalPreview && !multiple">
          <div v-if="previewUrl" style="margin: 1rem 0; overflow: hidden; position: relative;" v-bind:style="previewStyle">
            <div v-if="value.image && !previewImageLoaded" v-bind:style="loadingStyle" v-bind:class="loadingClass">
              <div>
                <img v-bind:src="fileDomain + '/ajax-loader/transparent-black-16x16.svg'"/><br/>
                {{ bundle.id_12197 }}
              </div>
            </div>
            <template v-if="value.image && croppingEnabled">
              <!--            <img v-bind:id="id + ':preview'"
                               v-bind:src="previewUrl"
                               style="display: block; max-width: 100%;">-->
              <cropper
                  class="cropper preview-ready"
                  v-bind:src="previewUrl"
                  v-bind:debounce="false"
                  v-bind:min-width="cropperMinWidth"
                  v-bind:min-height="cropperMinHeight"
                  v-bind:max-width="cropperMaxWidth"
                  v-bind:max-height="cropperMaxHeight"
                  v-bind:stencil-props="cropperStencilProps"
                  v-bind:default-size="cropperDefaultSize"
                  v-on:change="cropChange"/>
            </template>
            <template v-else="">
              <a v-bind:href="previewUrl" target="_blank" class="preview-ready">
                <img v-if="value.image"
                     v-bind:src="previewUrl"
                     v-on:load="previewImageLoaded = true"
                     style="display: block; max-width: 100%;">
                <template v-else="">{{ bundle.id_10589 }}</template>
              </a>
            </template>
          </div>
          <div v-if="croppingEnabled && !emptyValue && value.image" style="margin: 1rem 0;">
            <button v-if="cropWidth && cropHeight && !isMinimumSize" type="button" v-on:click.prevent="crop()"
                    class="btn btn-primary me-2">
              {{ bundle.id_10738 }} {{ cropDimension }}
            </button>
            <button v-if="undos.length" type="button" v-on:click.prevent="undo()" class="btn btn-primary">
              {{ bundle.id_10739 }}
            </button>
          </div>
        </template>
        <template v-if="externalPreview && !emptyValue">
          <slot name="preview" v-bind:value="value"></slot>
        </template>
      </div>
    </div>
    <local-errors v-bind:value="allErrors"/>
    <template v-if="help" v-slot:help="">
      <slot name="help"></slot>
    </template>
  </input-custom>
</template>

<script>
import Vue from "vue";
import Axios from "axios"
// https://norserium.github.io/vue-advanced-cropper/introduction/getting-started.html#package-installation
import {Cropper} from "vue-advanced-cropper"
import "vue-advanced-cropper/dist/style.css"

export default {
  components: {
    Cropper
  },
  props: {
    id: {
      type: String,
      required: true
    },
    uploadUrl: {
      type: String,
      required: true
    },
    cropUrl: {
      type: String,
      required: true
    },
    value: {},
    errors: {
      type: Array
    },
    fileDomain: {
      type: String,
      default: "https://www.nordiskemedier.dk"
    },
    croppingEnabled: {
      type: Boolean,
      default: false
    },
    aspectRatioSufficient: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number
    },
    height: {
      type: Number
    },
    minWidth: {
      type: Number
    },
    minHeight: {
      type: Number
    },
    maxWidth: {
      type: Number
    },
    maxHeight: {
      type: Number
    },
    maxFiles: {
      type: Number
    },
    label: {
      type: String
    },
    language: {
      type: String
    },
    required: {
      type: Boolean
    },
  },
  data: function () {
    return {
      bundle: {
        id_10587: "Choose File",
        id_11746: "Choose File(s)",
        id_10586: "Choose New File",
        id_11747: "Add File(s)",
        id_10588: "Remove File",
        id_10589: "Click here to see the current file",
        id_10738: "Crop",
        id_10739: "Undo",
        id_10591: "An unexpected error occurred.",
        id_10795: "Only archives can be uploaded. For example ZIP files.",
        id_10592: "Only pictures can be uploaded.",
        id_11189: "Only PDF files can be uploaded.",
        id_9660: "File type not allowed.",
        id_192: "The aspect ratio of the picture must be {0}. For example {1}*{2} pixels.",
        id_193: "Width of the picture must be exactly {0} pixels.",
        id_194: "Height of the picture must be exactly {0} pixels.",
        id_188: "Width of the picture must not be less than {0} pixels.",
        id_190: "Height of the picture must not be less than {0} pixels.",
        id_189: "Width of the picture must not exceed {0} pixels.",
        id_191: "Height of the picture must not exceed {0} pixels.",
        id_195: "The file is too big. It may not exceed {0}.",
        id_11738: "Drop the file here",
        id_11739: "Drop the file or files here",
        id_12197: "If your image does not appear here, it may be because you have an adblocker. Turn it off and try again."
      },
      status: {},
      internalErrors: [],
      cropX: null,
      cropY: null,
      cropWidth: null,
      cropHeight: null,
      cropMax: 800,
      cropMaxDefault: 800,
      undos: [],
      dragenter: 0,
      onDragTarget: false,
      previewImageLoaded: false,
    };
  },
  created: function () {
    Vue.$bundle("portal", this.bundle, (bundle) => {
      this.bundle = bundle;
    });
    this.$nextTick(() => {
      this.handleResize();
    })
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("dragover", this.onDragOver, false);
    document.addEventListener("dragenter", this.onDragEnter, false);
    document.addEventListener("dragleave", this.onDragLeave, false);
    document.addEventListener("drop", this.onDrop, false);
  },
  destroyed: function () {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("dragover", this.onDragOver);
    document.removeEventListener("dragenter", this.onDragEnter);
    document.removeEventListener("dragleave", this.onDragLeave);
    document.removeEventListener("drop", this.onDrop);
  },
  computed: {
    previewUrl: function () {
      if (!this.multiple && !this.emptyValue) {
        return this.fileDomain + "/" + this.value.code + "." + this.value.extension;
      }
      return null;
    },
    previewFactor: function () {
      if (this.value.width > this.cropMax) {
        return this.cropMax / this.value.width;
      }
      return 1;
    },
    previewStyle: function () {
      var wi = this.value.width * this.previewFactor;
      var hi = this.value.height * this.previewFactor;
      var style = "";
      if (this.value.image) {
        if (this.previewImageLoaded) {
          style += "max-width: " + wi + "px;";
          style += "max-height: " + hi + "px;";
        } else {
          style += "width: " + wi + "px;";
          style += "height: " + hi + "px;";
        }
      }
      return style;
    },
    loadingStyle: function () {
      var wi = this.value.width * this.previewFactor;
      var hi = this.value.height * this.previewFactor;
      var style = "";
      style += "width: " + wi + "px;";
      style += "height: " + hi + "px;";
      return style;
    },
    loadingClass: function () {
      var wi = this.value.width * this.previewFactor;
      var hi = this.value.height * this.previewFactor;
      var cls = "cropper-loading-class";
      if (wi > 600) {
        cls += " w600";
      } else if (wi > 500) {
        cls += " w500";
      } else if (wi > 400) {
        cls += " w400";
      }
      if (hi > 400) {
        cls += " h400";
      } else if (hi > 300) {
        cls += " h300";
      } else if (hi > 200) {
        cls += " h200";
      } else if (hi > 150) {
        cls += " h150";
      }
      return cls;
    },
    cropDimension: function () {
      return "[" + this.cropWidth + "x" + this.cropHeight + "]";
    },
    externalPreview: function () {
      var slots = this.$scopedSlots;
      var slot = slots && slots.preview && slots.preview();
      return !!(slot && slot.length);
    },
    help: function () {
      var slots = this.$scopedSlots;
      var slot = slots && slots.help && slots.help();
      return !!(slot && slot.length);
    },
    invalid: function () {
      return this.allErrors && this.allErrors.length > 0;
    },
    multiple: function () {
      return Array.isArray(this.value);
    },
    emptyValue: function () {
      return (this.multiple && this.value.length == 0) || (!this.multiple && !this.value);
    },
    uploading: function () {
      for (var index in this.status) {
        if (this.status[index].uploading) {
          return true;
        }
      }
      return false;
    },
    uploadProgress: function () {
      var total = Object.keys(this.status).length * 100;
      var progress = 0;
      for (var index in this.status) {
        var status = this.status[index];
        if (status.uploadProgress) {
          progress += (100 * status.uploadProgress / total);
        }
      }
      return Math.round(progress);
    },
    cropperMinWidth: function () {
      if (this.minWidth) {
        return this.minWidth;
      }
      return 1;
    },
    cropperMinHeight: function () {
      if (this.minHeight) {
        return this.minHeight;
      }
      return 1;
    },
    cropperMaxWidth: function () {
      if (this.maxWidth) {
        return this.maxWidth;
      }
      return this.value.width;
    },
    cropperMaxHeight: function () {
      if (this.maxHeight) {
        return this.maxHeight;
      }
      return this.value.height;
    },
    cropperStencilProps: function () {
      var props = {};
      if (this.width && this.height) {
        props.aspectRatio = this.width / this.height;
      }
      var handlers = {};
      var hideHandlers = true;
      if (this.cropperMinWidth != this.cropperMaxWidth && this.cropperMinHeight != this.cropperMaxHeight) {
        handlers.westNorth = true;
        handlers.eastNorth = true;
        handlers.eastSouth = true;
        handlers.westSouth = true;
        hideHandlers = false;
      }
      if (this.cropperMinWidth != this.cropperMaxWidth && this.cropperMinHeight == this.cropperMaxHeight) {
        handlers.west = true;
        handlers.east = true;
        hideHandlers = false;
      }
      if (this.cropperMinWidth == this.cropperMaxWidth && this.cropperMinHeight != this.cropperMaxHeight) {
        handlers.north = true;
        handlers.south = true;
        hideHandlers = false;
      }
      props.handlers = handlers;
      if (hideHandlers) {
        props.resizable = false;
      }
      return props;
    },
    isMinimumSize: function () {
      return this.value && this.value.width <= this.cropperMinWidth && this.value.height <= this.cropperMinHeight && this.isCorrectAspectRatio(this.value);
    },
    dragging: function () {
      return this.dragenter > 0 && !this.maxFilesReached;
    },
    maxFilesReached: function () {
      return this.maxFiles && this.multiple && this.maxFiles <= this.value.length;
    },
    allErrors: function () {
      var array = [];
      if (this.internalErrors && this.internalErrors.length) {
        array.push(...this.internalErrors);
      }
      if (this.errors && this.errors.length) {
        array.push(...this.errors);
      }
      return array;
    }
  },
  watch: {
    value: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        if (oldValue && !Array.isArray(oldValue)) {
          this.undos.push(oldValue);
        }
      }
    }
  },
  methods: {
    handleResize: function () {
      var max = this.cropMaxDefault;
      const ref = this.$refs.preview;
      if (ref) {
        const rect = ref.getBoundingClientRect();
        if (rect && rect.width && rect.width < max) {
          max = rect.width;
        }
      }
      this.cropMax = max;
    },
    onDragOver: function (event) {
      event.preventDefault();
    },
    onDragEnter: function (event) {
      if (!this.onDragTarget && event.target.id == this.id) {
        this.onDragTarget = true;
      }
      this.dragenter++;
    },
    onDragLeave: function (event) {
      if (this.onDragTarget && event.target.id == this.id) {
        this.onDragTarget = false;
      }
      this.dragenter--;
    },
    onDrop: function (event) {
      event.preventDefault();
      try {
        if (event.target.id == this.id) {
          var len = Object.keys(this.status).length;
          var handleFile = (file) => {
            file.index = len++;
            this.handleFile(file);
          }
          if (event.dataTransfer.items) {
            // Use DataTransferItemList interface to access the file(s)
            for (var i = 0; i < event.dataTransfer.items.length; i++) {
              // If dropped items aren't files, reject them
              if (event.dataTransfer.items[i].kind === 'file') {
                var file1 = event.dataTransfer.items[i].getAsFile();
                handleFile(file1);
                if (!this.multiple) {
                  break;
                }
              }
            }
          } else {
            // Use DataTransfer interface to access the file(s)
            for (var j = 0; j < event.dataTransfer.files.length; j++) {
              var file2 = event.dataTransfer.files[j];
              handleFile(file2);
              if (!this.multiple) {
                break;
              }
            }
          }
        }
      } finally {
        this.onDragTarget = false;
        this.dragenter = 0;
      }
    },
    addFile: function (file) {
      if (this.multiple) {
        if (!this.maxFilesReached) {
          this.previewImageLoaded = false;
          this.value.push(file);
          this.updateValue(this.value);
        }
      } else {
        this.previewImageLoaded = false;
        this.updateValue(file);
      }
    },
    handleFile: function (file) {
      var formData = new FormData();
      formData.append("file", file);
      this.internalErrors = [];
      this.$set(this.status, file.index, {
        uploading: true,
        uploadProgress: 0
      });
      Axios(
          {
            url: this.uploadUrl,
            method: "post",
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data"
            },
            onUploadProgress: (progressEvent) => {
              this.status[file.index].uploadProgress = Math.floor(100 * progressEvent.loaded / progressEvent.total);
            }
          }
      )
          .then((response) => {
            var data = response.data;
            if (data.status) {
              if (data.status.toLowerCase() === "success" && data.file) {
                if (!this.croppingEnabled) {
                  if (this.aspectRatioSufficient) {
                    if (this.width && this.height) {
                      if (!this.isCorrectAspectRatio(data.file)) {
                        var requiredRatio = this.width / this.height;
                        this.internalErrors.push(this.bundle.id_192
                            .replace("{0}", requiredRatio)
                            .replace("{1}", this.width)
                            .replace("{2}", this.height));
                      }
                    }
                  } else {
                    if (this.width && data.file.width != this.width) {
                      this.internalErrors.push(this.bundle.id_193.replace("{0}", this.width));
                    }
                    if (this.height && data.file.height != this.height) {
                      this.internalErrors.push(this.bundle.id_194.replace("{0}", this.height));
                    }
                  }
                }
                if (this.minWidth && data.file.width < this.minWidth) {
                  this.internalErrors.push(this.bundle.id_188.replace("{0}", this.minWidth));
                }
                if (this.minHeight && data.file.height < this.minHeight) {
                  this.internalErrors.push(this.bundle.id_190.replace("{0}", this.minHeight));
                }
                if (!this.croppingEnabled) {
                  if (this.maxWidth && data.file.width > this.maxWidth) {
                    this.internalErrors.push(this.bundle.id_189.replace("{0}", this.maxWidth));
                  }
                  if (this.maxHeight && data.file.height > this.maxHeight) {
                    this.internalErrors.push(this.bundle.id_191.replace("{0}", this.maxHeight));
                  }
                }
                if (this.internalErrors.length == 0) {
                  this.addFile(data.file);
                }
              } else {
                var msg;
                switch (data.status.toLowerCase()) {
                  case "archive_required":
                    msg = this.bundle.id_10795;
                    break;
                  case "image_required":
                    msg = this.bundle.id_10592;
                    break;
                  case "pdf_required":
                    msg = this.bundle.id_11189;
                    break;
                  case "max_size_exceeded":
                    msg = this.bundle.id_195;
                    break;
                  default:
                    console.log("Unsupported status: " + data.status);
                    msg = this.bundle.id_10591;
                }
                if (data.statusParams && data.statusParams.length) {
                  for (var i = 0; i < data.statusParams.length; i++) {
                    msg = msg.replace("{" + i + "}", data.statusParams[i]);
                  }
                }
                this.internalErrors.push(msg);
              }
            }
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            this.status[file.index].uploading = false;
            var allDone = true;
            for (var index1 in this.status) {
              if (this.status[index1].uploading) {
                allDone = false;
                break;
              }
            }
            if (allDone) {
              for (var index2 in this.status) {
                if (!this.status[index2].uploading) {
                  delete this.status[index2];
                }
              }
            }
          });
    },
    handleUpload: function () {
      var len = Object.keys(this.status).length;
      for (var i = 0; i < this.$refs.file.files.length; i++) {
        var file = this.$refs.file.files[i];
        file.index = len + i;
        this.handleFile(file);
        if (!this.multiple) {
          break;
        }
      }
    },
    cropChange: function ({coordinates}) {
      this.cropX = coordinates.left;
      this.cropY = coordinates.top;
      this.cropWidth = coordinates.width;
      this.cropHeight = coordinates.height;
    },
    crop: function () {
      if (this.value && this.cropX >= 0 && this.cropY >= 0 && this.cropWidth > 3 && this.cropHeight > 3) {
        // Recalculate height according to the required dimensions. We need to do this because we scale the
        // image when we crop it, so the crop selection might be a little off, which later will result in
        // validation errors server-side.
        // This recalculation matches the height calculations in our ImageValidator.
        if (this.width && this.height) {
          this.cropHeight = Math.round(this.height / (this.width / this.cropWidth));
        }
        Axios(
            {
              url: this.cropUrl + "?id=" + this.value.id + "&x=" + this.cropX + "&y=" + this.cropY + "&w=" + this.cropWidth + "&h=" + this.cropHeight,
              method: "post"
            }
        )
            .then((response) => {
              this.updateValue(response.data.file);
            })
            .catch((err) => {
              console.log(err);
            });
      }
    },
    cropperDefaultSize: function ({imageSize, visibleArea}) {
      return {
        width: (visibleArea || imageSize).width,
        height: (visibleArea || imageSize).height,
      };
    },
    isCorrectAspectRatio: function (file) {
      if (file && file.width && file.height && this.width && this.height) {
        // Instead of calculating aspect ratios and comparing two numbers with endless decimals,
        // we instead calculate what the current image would be resized to, if we resized it to the
        // desired width and height. This allows us to round the numbers and avoid decimals, which
        // are irrelevant anyhow, once the image is resized to an actual pixel size.
        var widthRatio = file.width / this.width;
        var heightRatio = file.height / this.height;
        var width = Math.round(file.width / widthRatio);
        var height = Math.round(file.height / heightRatio);
        return width == this.width && height == this.height;
        // var requiredRatio = this.width / this.height;
        // var actualRatio = data.file.width / data.file.height;
        // return requiredRatio == actualRatio;
      }
      return false;
    },
    undo: function () {
      if (this.undos.length > 0) {
        var file = this.undos.splice(this.undos.length - 1, 1)[0];
        this.updateValue(file);
        this.$nextTick(() => {
          this.undos.splice(this.undos.length - 1, 1);
        });
      }
    },
    removeValue: function () {
      this.updateValue(null);
      this.$nextTick(() => {
        this.undos.splice(0, this.undos.length);
      });
    },
    updateValue: function (value) {
      this.$emit("input", value);
    }
  }
}
</script>

<style lang="less">
.cropper-loading-class {
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  top: 0;
  div {
    text-align: center;
  }
  &.w400 {
    padding: 0 10%;
  }
  &.w500 {
    padding: 0 20%;
  }
  &.w600 {
    padding: 0 25%;
  }
  img {
    height: 25px;
  }
  &.h150 {
    img {
      margin-bottom: .25rem;
    }
  }
  &.h200 {
    img {
      height: 30px;
      margin-bottom: .5rem;
    }
  }
  &.h300 {
    img {
      height: 40px;
      margin-bottom: .75rem;
    }
  }
  &.h400 {
    img {
      height: 50px;
      margin-bottom: 1rem;
    }
  }
}
.preview-ready {
  position: relative;
  z-index: 3;
}
</style>